/**
 * The public docs frame: a fixed top bar (logo, the section's name, search on
 * inner Help pages, contact), the royal ambient wash every public page
 * carries, and the site footer. Public: no sign-in, outside the app Layout.
 * The Help Center and the Security pages share it; `homeLabel`/`homeHref`
 * name the section in the bar.
 */
import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import Footer from '@/components/Footer';
import HelpSearch from './HelpSearch';

export default function HelpShell({ children, showSearch = true, homeLabel = 'Help Center', homeHref = '/help' }) {
  const { pathname } = useLocation();
  // A new article starts at the top; an in-page anchor keeps its place.
  useEffect(() => {
    if (!window.location.hash) window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <div className="min-h-screen bg-black text-white">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[520px]"
        style={{ background: 'radial-gradient(ellipse 80% 70% at 50% -10%, rgba(15,25,82,0.75), rgba(10,18,51,0.25) 45%, transparent 75%)' }}
      />
      <header className="fixed inset-x-0 top-0 z-40 border-b border-white/[0.08] bg-black/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center gap-4 px-4 sm:px-6">
          <Link to="/" className="flex-shrink-0" aria-label="Defera home">
            <img src="/defera-logo-web.png" alt="Defera" className="h-7 w-auto" />
          </Link>
          <span className="hidden sm:block h-5 w-px bg-white/15" aria-hidden />
          <Link to={homeHref} className="hidden sm:block text-[14px] font-medium text-white/85 hover:text-white whitespace-nowrap">
            {homeLabel}
          </Link>
          <div className="ml-auto flex min-w-0 flex-1 items-center justify-end gap-3">
            {showSearch && (
              <div className="w-full max-w-sm">
                <HelpSearch variant="compact" />
              </div>
            )}
            <Link to="/contact" className="hidden md:block text-[14px] text-white/65 hover:text-white whitespace-nowrap">
              Contact us
            </Link>
            <Link
              to="/search"
              className="hidden sm:inline-flex items-center rounded-full bg-white px-4 py-1.5 text-[13.5px] font-medium text-black hover:bg-white/90 whitespace-nowrap"
            >
              Search flights
            </Link>
          </div>
        </div>
      </header>
      <main className="relative pt-16">{children}</main>
      <Footer />
    </div>
  );
}
HelpShell.propTypes = { children: PropTypes.node, showSearch: PropTypes.bool, homeLabel: PropTypes.string, homeHref: PropTypes.string };
